import React, { ReactNode, useCallback, useContext, useMemo } from 'react'
import { FieldErrors, useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import isEmpty from 'lodash/isEmpty'
import { FormInput } from '@microservices/wiskey-react-components'
import { Add } from '@mui/icons-material'
import { Box, Button } from '@mui/material'

import { BindTypeInputs } from '@components/BindTypeInputs'
import { INPUT_TYPE } from '@components/BindTypeInputs/ValueInputFactory'

import { getParamsString } from '@helpers'
import { GENERATOR_INPUT_TYPE } from '@constants'
import { AutocompleteOption } from '@types'
import { ConstFormat } from '@formats'

import { AccordionConfigExamples } from '@gantt/components/ConfigExamples'
import {
  AvailableVariables,
  BACKGROUND_BINDING_TYPE,
  BindField,
  BORDERS_WIDTH_BINDING_TYPE,
  GANTT_BIND_TYPE_LIST,
  LINES_WIDTH_BINDING_TYPE,
  PageContext,
  SegmentFieldType,
  TITLE_BINDING_TYPE,
} from '@gantt/components/GanttCreateOrEdit'
import {
  displayToggleOptions,
  GANTT_BAR_MAX_TITLES_COUNT,
  timelineItemTitleDefaultValue,
} from '@gantt/constants'
import { checkValidGanttJS, getBindTypeOptionsByEnum } from '@gantt/helpers'
import { checkValidGanttJSON } from '@gantt/helpers/checkValidGanttJSON'
import { GANTT_SCRIPT_RETURN_TYPE } from '@gantt/types'

import { VariablesList } from '../VariablesList'

type BaseFormFieldsProps = {
  disabled?: boolean
  isShowScriptDialog?: boolean
  isShownCornersInput?: boolean
  mainPath?: string
  watchedObject?: AutocompleteOption<string> | null
  toggleOpenScriptValueDialog: () => void
  currentBarKey?: string
  keyValue?: string
  variablesForJS?: AvailableVariables
  existingPaths?: string[]
  hiddenFields?: Map<string, boolean>
  errors?: FieldErrors<T>
  afterTitlesSlot?: ReactNode
}

const colorHints = {
  js: i18next.t('ganttCreate.timelineForm.hint.backgroundColorScriptHint'),
  static: i18next.t('ganttCreate.timelineForm.hint.backgroundColorStaticHint'),
}

const tooltipPlaceholders = {
  js: i18next.t('ganttCreate.timelineForm.placeholder.tooltipJS'),
}

const titleHints = {
  js: i18next.t('ganttCreate.timelineForm.hint.titleScriptHint'),
}

const titlePlaceholders = {
  js: i18next.t('ganttCreate.timelineForm.placeholder.titleJS'),
}

export const BaseFormFields = ({
  disabled = false,
  isShowScriptDialog = false,
  isShownCornersInput = false,
  mainPath,
  watchedObject,
  toggleOpenScriptValueDialog,
  keyValue,
  currentBarKey,
  variablesForJS,
  existingPaths,
  hiddenFields,
  errors,
  afterTitlesSlot,
}: BaseFormFieldsProps) => {
  const { watch, control } = useFormContext()
  const { availableVariables } = useContext(PageContext)
  const { t } = useTranslation()
  const variables = variablesForJS || availableVariables

  const isShown = (key: string) => !hiddenFields?.get(getPath(key))
  const isEmptyField = (key: string) => isEmpty(getParamsString(watch(getPath(key))))

  const getPath = (path: string) => {
    if (mainPath) {
      return `${mainPath}.${path}`
    }

    return `${path}`
  }

  const watchedColorBindType = watch(getPath('background'))?.bindType

  const {
    fields: titles,
    append: appendTitle,
    remove: removeTitle,
  } = useFieldArray({ control, name: getPath('titles') })

  const getPrefix = (type: SegmentFieldType) => {
    return [currentBarKey, keyValue, type].filter(s => !!s).join('_')
  }

  const funValidator = (
    value: string,
    returnValueType: GANTT_SCRIPT_RETURN_TYPE,
    keys?: string[],
    bindType?: GANTT_BIND_TYPE_LIST.JS | GANTT_BIND_TYPE_LIST.JSON
  ) => {
    if (bindType === GANTT_BIND_TYPE_LIST.JSON) {
      return checkValidGanttJSON(value, returnValueType, keys)
    }

    return checkValidGanttJS(value, variables, returnValueType, keys)
  }

  const funValidatorString = useCallback((value: string) => {
    return funValidator(value, GANTT_SCRIPT_RETURN_TYPE.STRING)
  }, [])

  const funValidatorBoolean = useCallback((value: string) => {
    return funValidator(value, GANTT_SCRIPT_RETURN_TYPE.BOOLEAN)
  }, [])

  const fieldPicker = useMemo(() => {
    return {
      existingPaths,
    }
  }, [existingPaths])

  const getColorHints = (isEmptyValue?: boolean) => {
    return {
      js: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : colorHints.js,
      static: isEmptyValue
        ? i18next.t('ganttCreate.timelineForm.hint.defaultHint')
        : colorHints.static,
    }
  }

  const getWidthHints = useCallback((isEmptyValue?: boolean) => {
    return {
      js: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : '',
      static: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : '',
    }
  }, [])

  const getSegmentHeightHints = useCallback(() => {
    return {
      js: i18next.t('ganttCreate.timelineForm.hint.segmentHeightHint'),
      static: i18next.t('ganttCreate.timelineForm.hint.segmentHeightHint'),
    }
  }, [])

  const getTypeHints = useCallback((isEmptyValue?: boolean) => {
    return {
      js: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : '',
      static: isEmptyValue ? i18next.t('ganttCreate.timelineForm.hint.defaultHint') : '',
    }
  }, [])

  const segmentHeightError = errors?.segmentHeight ? errors.segmentHeight : undefined

  const handleAddTitle = () => {
    appendTitle({ ...timelineItemTitleDefaultValue })
  }

  const handleDeleteTitle = (index: number) => {
    removeTitle(index)
  }

  return (
    <>
      {isShown('segmentHeight') && (
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.segmentHeight')}</legend>
          {isShown('segmentHeight') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BORDERS_WIDTH_BINDING_TYPE)}
              containerName={getPath('segmentHeight')}
              errorMessage={segmentHeightError?.message}
              fieldPicker={fieldPicker}
              hintDict={getSegmentHeightHints()}
              inputType={INPUT_TYPE.NUMBER}
              isDisabled={disabled}
              object={watchedObject}
              placeholderDict={tooltipPlaceholders}
              numberInput={{
                max: 80,
              }}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
        </Box>
      )}
      {isShown('title') && (
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.titles')}</legend>
          {(titles as BindField[])?.map((title, index: number) => {
            return (
              <div key={`${title.id}_${index}`}>
                <BindTypeInputs
                  isFieldsetMarkup
                  isShowFormatDateField
                  bindTypeOptions={getBindTypeOptionsByEnum(TITLE_BINDING_TYPE)}
                  containerName={getPath(`titles.${index}`)}
                  fieldPicker={fieldPicker}
                  fieldsetTitle={`${t('ganttCreate.timelineForm.row')} ${index + 1}`}
                  hintDict={titleHints}
                  isDisabled={disabled}
                  object={watchedObject}
                  placeholderDict={titlePlaceholders}
                  prefix={`${getPrefix(SegmentFieldType.TITLE)}_${index}`}
                  scriptEditor={{
                    validator: funValidatorString,
                  }}
                  onDelete={() => handleDeleteTitle(index)}
                />
                {!!title.bindType && [GANTT_BIND_TYPE_LIST.JS].includes(title.bindType) && (
                  <AccordionConfigExamples>
                    <VariablesList variables={variables} />
                  </AccordionConfigExamples>
                )}
              </div>
            )
          })}
          {titles?.length < GANTT_BAR_MAX_TITLES_COUNT && (
            <Box display='flex' justifyContent='end' pb={1}>
              <Button onClick={handleAddTitle}>
                <Add fontSize='small' />
                {t('ganttCreate.timelineForm.addTitle')}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {afterTitlesSlot}
      {isShown('background') && (
        <>
          <BindTypeInputs
            isFieldsetMarkup
            bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
            containerName={getPath('background')}
            fieldsetTitle={t('ganttCreate.timelineForm.background')}
            hintDict={colorHints}
            inputType={INPUT_TYPE.COLOR}
            object={watchedObject}
            scriptEditor={{
              validator: funValidatorString,
            }}
          />
          {[BACKGROUND_BINDING_TYPE.JS].includes(watchedColorBindType) && (
            <AccordionConfigExamples>
              <VariablesList variables={variables} />
            </AccordionConfigExamples>
          )}
        </>
      )}
      {isShown('boxVerticalGuideLines') && (
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.boxVerticalGuideLines.blockLabel')}</legend>
          {isShown('boxVerticalGuideLines.has') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              containerName={getPath('boxVerticalGuideLines.has')}
              inputType={INPUT_TYPE.TOGGLE}
              blockLabel={t(
                'ganttCreate.timelineForm.boxVerticalGuideLines.hasBoxVerticalGuideLines'
              )}
              scriptEditor={{
                validator: funValidatorBoolean,
              }}
              toggleButton={{
                toggleButtonOptions: displayToggleOptions,
              }}
            />
          )}
          {isShown('boxVerticalGuideLines.isBoxColor') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BORDERS_WIDTH_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.boxVerticalGuideLines.isBoxColor')}
              containerName={getPath('boxVerticalGuideLines.isBoxColor')}
              inputType={INPUT_TYPE.TOGGLE}
              scriptEditor={{
                validator: funValidatorBoolean,
              }}
              toggleButton={{
                toggleButtonOptions: displayToggleOptions,
              }}
            />
          )}
          {isShown('boxVerticalGuideLines.color') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.boxVerticalGuideLines.color')}
              containerName={getPath('boxVerticalGuideLines.color')}
              hintDict={getColorHints(isEmptyField('boxVerticalGuideLines.color'))}
              inputType={INPUT_TYPE.COLOR}
              object={watchedObject}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
          {isShown('boxVerticalGuideLines.width') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BORDERS_WIDTH_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.boxVerticalGuideLines.width')}
              containerName={getPath('boxVerticalGuideLines.width')}
              fieldPicker={fieldPicker}
              hintDict={getWidthHints(isEmptyField('boxVerticalGuideLines.width'))}
              inputType={INPUT_TYPE.NUMBER}
              isDisabled={disabled}
              object={watchedObject}
              placeholderDict={tooltipPlaceholders}
              numberInput={{
                max: 5,
              }}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
          {isShown('boxVerticalGuideLines.type') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.boxVerticalGuideLines.type')}
              containerName={getPath('boxVerticalGuideLines.type')}
              hintDict={getTypeHints(isEmptyField('boxVerticalGuideLines.type'))}
              object={watchedObject}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
        </Box>
      )}
      {isShown('borders.inner') && (
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.innerBorders.blockLabel')}</legend>
          {isShown('borders.inner.has') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.innerBorders.hasInnerBorders')}
              containerName={getPath('borders.inner.has')}
              inputType={INPUT_TYPE.TOGGLE}
              scriptEditor={{
                validator: funValidatorBoolean,
              }}
              toggleButton={{
                toggleButtonOptions: displayToggleOptions,
              }}
            />
          )}
          {isShown('borders.inner.color') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.innerBorders.color')}
              containerName={getPath('borders.inner.color')}
              hintDict={getColorHints(isEmptyField('borders.inner.color'))}
              inputType={INPUT_TYPE.COLOR}
              object={watchedObject}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
          {isShown('borders.inner.width') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BORDERS_WIDTH_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.innerBorders.width')}
              containerName={getPath('borders.inner.width')}
              fieldPicker={fieldPicker}
              hintDict={getWidthHints(isEmptyField('borders.inner.width'))}
              inputType={INPUT_TYPE.NUMBER}
              isDisabled={disabled}
              object={watchedObject}
              placeholderDict={tooltipPlaceholders}
              numberInput={{
                max: 5,
              }}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
        </Box>
      )}
      {isShown('borders.outer') && (
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.outerBorders.blockLabel')}</legend>
          {isShown('borders.outer.has') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.outerBorders.hasOuterBorders')}
              containerName={getPath('borders.outer.has')}
              inputType={INPUT_TYPE.TOGGLE}
              scriptEditor={{
                validator: funValidatorBoolean,
              }}
              toggleButton={{
                toggleButtonOptions: displayToggleOptions,
              }}
            />
          )}
          {isShown('borders.outer.color') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.outerBorders.color')}
              containerName={getPath('borders.outer.color')}
              hintDict={getColorHints(isEmptyField('borders.outer.color'))}
              inputType={INPUT_TYPE.COLOR}
              object={watchedObject}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
          {isShown('borders.outer.width') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BORDERS_WIDTH_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.outerBorders.width')}
              containerName={getPath('borders.outer.width')}
              fieldPicker={fieldPicker}
              hintDict={getWidthHints(isEmptyField('borders.outer.width'))}
              inputType={INPUT_TYPE.NUMBER}
              isDisabled={disabled}
              object={watchedObject}
              placeholderDict={tooltipPlaceholders}
              numberInput={{
                max: 5,
              }}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
        </Box>
      )}
      {isShown('lines.top') && (
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.linesTop.blockLabel')}</legend>
          {
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.linesTop.hasLinesTop')}
              containerName={getPath('lines.top.has')}
              inputType={INPUT_TYPE.TOGGLE}
              scriptEditor={{
                validator: funValidatorBoolean,
              }}
              toggleButton={{
                toggleButtonOptions: displayToggleOptions,
              }}
            />
          }
          {
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.linesTop.color')}
              containerName={getPath('lines.top.color')}
              hintDict={getColorHints(isEmptyField('lines.top.color'))}
              inputType={INPUT_TYPE.COLOR}
              object={watchedObject}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          }
          {
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(LINES_WIDTH_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.linesTop.width')}
              containerName={getPath('lines.top.width')}
              fieldPicker={fieldPicker}
              hintDict={getWidthHints(isEmptyField('lines.top.width'))}
              inputType={INPUT_TYPE.NUMBER}
              isDisabled={disabled}
              object={watchedObject}
              placeholderDict={tooltipPlaceholders}
              numberInput={{
                max: 5,
              }}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          }
        </Box>
      )}
      {isShown('lines.bottom') && (
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.linesBottom.blockLabel')}</legend>
          {isShown('lines.bottom.has') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.linesBottom.hasLinesBottom')}
              containerName={getPath('lines.bottom.has')}
              inputType={INPUT_TYPE.TOGGLE}
              scriptEditor={{
                validator: funValidatorBoolean,
              }}
              toggleButton={{
                toggleButtonOptions: displayToggleOptions,
              }}
            />
          )}
          {
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.linesBottom.color')}
              containerName={getPath('lines.bottom.color')}
              hintDict={getColorHints(isEmptyField('lines.bottom.color'))}
              inputType={INPUT_TYPE.COLOR}
              object={watchedObject}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          }
          {
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(LINES_WIDTH_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.linesBottom.width')}
              containerName={getPath('lines.bottom.width')}
              fieldPicker={fieldPicker}
              hintDict={getWidthHints(isEmptyField('lines.bottom.width'))}
              inputType={INPUT_TYPE.NUMBER}
              isDisabled={disabled}
              object={watchedObject}
              placeholderDict={tooltipPlaceholders}
              numberInput={{
                max: 5,
              }}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          }
        </Box>
      )}
      {
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.activeSegment.blockLabel')}</legend>
          {isShown('activeSegment.has') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.activeSegment.hasActiveSegment')}
              containerName={getPath('activeSegment.has')}
              inputType={INPUT_TYPE.TOGGLE}
              scriptEditor={{
                validator: funValidatorBoolean,
              }}
              toggleButton={{
                toggleButtonOptions: displayToggleOptions,
              }}
            />
          )}
          {isShown('activeSegment.color') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.activeSegment.color')}
              containerName={getPath('activeSegment.color')}
              hintDict={getColorHints(isEmptyField('activeSegment.color'))}
              inputType={INPUT_TYPE.COLOR}
              object={watchedObject}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
          {isShown('activeSegment.opacity') && (
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label={i18next.t('ganttCreate.timelineForm.activeSegment.opacity')}
              name={getPath('activeSegment.opacity')}
              placeholder={i18next.t('ganttCreate.timelineForm.placeholder.activeSegment.opacity')}
              textFieldInputProps={{
                inputComponent: ConstFormat as any,
                inputProps: { min: 0, max: 1 },
              }}
            />
          )}
          {isShown('activeSegment.verticalMargin') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BORDERS_WIDTH_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.activeSegment.verticalMargin')}
              containerName={getPath('activeSegment.verticalMargin')}
              fieldPicker={fieldPicker}
              hintDict={getWidthHints(isEmptyField('activeSegment.verticalMargin'))}
              inputType={INPUT_TYPE.NUMBER}
              isDisabled={disabled}
              object={watchedObject}
              placeholderDict={tooltipPlaceholders}
              numberInput={{
                max: 50,
              }}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
          {isShown('activeSegment.sideMargin') && (
            <BindTypeInputs
              bindTypeOptions={getBindTypeOptionsByEnum(BORDERS_WIDTH_BINDING_TYPE)}
              blockLabel={t('ganttCreate.timelineForm.activeSegment.sideMargin')}
              containerName={getPath('activeSegment.sideMargin')}
              fieldPicker={fieldPicker}
              hintDict={getWidthHints(isEmptyField('activeSegment.sideMargin'))}
              inputType={INPUT_TYPE.NUMBER}
              isDisabled={disabled}
              object={watchedObject}
              placeholderDict={tooltipPlaceholders}
              numberInput={{
                max: 50,
              }}
              scriptEditor={{
                validator: funValidatorString,
              }}
            />
          )}
        </Box>
      }
      {isShown('textColor') && (
        <BindTypeInputs
          isFieldsetMarkup
          bindTypeOptions={getBindTypeOptionsByEnum(BACKGROUND_BINDING_TYPE)}
          containerName={getPath('textColor')}
          fieldsetTitle={t('ganttCreate.timelineForm.textColor')}
          hintDict={getColorHints(isEmpty(watch(getPath('textColor'))))}
          inputType={INPUT_TYPE.COLOR}
          object={watchedObject}
          scriptEditor={{
            validator: funValidatorString,
          }}
        />
      )}
      {isShown('parameters') && (
        <Box component='fieldset'>
          <legend>{t('ganttCreate.timelineForm.parameters')}</legend>
          {isShown('hasCorners') && (
            <FormInput
              formLabelSx={{ width: '145px' }}
              inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
              label={t('ganttCreate.timelineForm.hasCorners')}
              labelPlacement='end'
              name={getPath('hasCorners')}
              labelSx={{
                minWidth: 'auto',
              }}
            />
          )}
        </Box>
      )}
    </>
  )
}
